import PublicLayout from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import {
  Radar, Sparkles, Globe, ShieldCheck, Workflow, Brain, ArrowRight,
  Target, Cpu, BarChart3,
} from "lucide-react";

const Stat = ({ value, label }) => (
  <div className="border border-[#262626] bg-[#141414] rounded-sm p-5">
    <div className="font-display text-3xl md:text-4xl text-white tracking-tighter">{value}</div>
    <div className="kpi-label mt-1.5">{label}</div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, body }) => (
  <div className="border border-[#262626] bg-[#141414] rounded-sm p-6 flex flex-col gap-3 hover:border-[#0A84FF]/50 transition-colors">
    <div className="w-10 h-10 rounded-sm border border-[#262626] bg-[#0A0A0A] flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#0A84FF]" strokeWidth={1.6}/>
    </div>
    <h3 className="font-display text-base text-white tracking-tight">{title}</h3>
    <p className="text-[13px] text-[#A3A3A3] leading-relaxed">{body}</p>
  </div>
);

export default function AboutPage() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="border-b border-[#1F1F1F]">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="kpi-label mb-3">/ ABOUT · RADAR</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter text-white leading-[1.02] max-w-4xl">
            An AI-native <span className="text-[#0A84FF]">GTM Intelligence Copilot</span> for modern B2B teams.
          </h1>
          <p className="text-[#A3A3A3] mt-6 text-[16px] md:text-[18px] leading-relaxed max-w-3xl">
            RADAR is built for Business Development professionals who don't have time to chase 12 browser
            tabs to figure out who a company is, what they care about, and how to open a conversation.
            We turn fragmented public + private signals into actionable, executive-grade intelligence —
            in under 90 seconds.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pricing"
              data-testid="about-cta-pricing"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white font-medium tracking-tight transition-colors"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.8}/>
              See pricing
              <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
            <Link
              to="/security"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-sm border border-[#262626] hover:border-[#404040] text-white font-medium tracking-tight transition-colors"
            >
              How we keep your data safe
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat value="21" label="Executive sections / report" />
          <Stat value="~90s" label="End-to-end research time" />
          <Stat value="5×" label="Parallel live web searches" />
          <Stat value="100%" label="Citation-grounded outputs" />
        </div>
      </section>

      {/* What it does */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-14">
        <div className="kpi-label mb-3">/ WHAT IT DOES</div>
        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter max-w-3xl">
          From <span className="text-[#0A84FF]">company name</span> to a polished sales motion — without the browser-tab gauntlet.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <FeatureCard
            icon={Brain}
            title="AI-Powered Research"
            body="Claude Haiku 4.5 synthesizes financials, leadership, tech stack, M&A activity, strategic priorities and more into a structured 21-section executive report."
          />
          <FeatureCard
            icon={Globe}
            title="Real-Time Web Grounding"
            body="Every report is anchored on live Tavily web search results. You see real citations, not hallucinations — and Section 21 lists every source by topic."
          />
          <FeatureCard
            icon={Target}
            title="Deal & Outreach Intelligence"
            body="Attractiveness Score, deal opportunity matrix, CXO email draft, and a 7-slide PPT storyline — generated alongside the research, ready to send."
          />
          <FeatureCard
            icon={Workflow}
            title="Proposition Generation"
            body="Layer your team's primary data — past projects, internal strengths, insider notes — and RADAR re-renders the report as a hyper-personalised proposition."
          />
          <FeatureCard
            icon={BarChart3}
            title="BDR Pipeline & Productivity"
            body="Log one activity per account. RADAR auto-derives stage, funnel conversion, cycle times, stalled deals, and team-level rollups — without CRM overhead."
          />
          <FeatureCard
            icon={Cpu}
            title="GTM Acceleration"
            body="Built for Engineering Services, B2B consulting, and enterprise SaaS sellers. Insight-led selling that closes the prep gap between research and pitch."
          />
        </div>
      </section>

      {/* Vision */}
      <section className="border-t border-[#1F1F1F] mt-6">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="kpi-label mb-3">/ OUR VISION</div>
          <h2 className="font-display text-3xl md:text-4xl text-white tracking-tighter max-w-3xl leading-tight">
            Make every BDR walk into every meeting <span className="text-[#0A84FF]">already smarter</span> than the room.
          </h2>
          <p className="text-[15px] text-[#A3A3A3] mt-5 max-w-3xl leading-relaxed">
            Account research today is a 3-hour Wikipedia-and-LinkedIn marathon for the rep, followed by
            half-baked decks for the CXO meeting. RADAR collapses that into a single, citation-grounded,
            executive-ready briefing — so reps spend their time on the conversation, not the prep.
            Our north star: zero-friction GTM intelligence for B2B teams of every size.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF] text-[10px] font-mono-data tracking-wider uppercase">
              <Radar className="w-3 h-3"/> Real-Time
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#32D74B]/40 bg-[#32D74B]/10 text-[#32D74B] text-[10px] font-mono-data tracking-wider uppercase">
              <ShieldCheck className="w-3 h-3"/> Citation-Grounded
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 text-[#FF9F0A] text-[10px] font-mono-data tracking-wider uppercase">
              <Sparkles className="w-3 h-3"/> AI-Native
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
